import * as z from 'zod/mini'
import {
  CreateCustomerBody,
  CreateCustomerVisitBody,
  customerFields,
  fromCalendarDate,
  ManagedCustomer,
  normalizeEmail,
  personFields,
  toCalendarDate,
  UpdateCustomerBody,
} from '@seiri/shared/common'
import { assertFormNumber, nonEmpty, requiredFormNumber } from './form-schema'

/**
 * Adding customers: a list of emails, one `POST .../customers` each. Every chip is checked with the
 * same email rule as `createCustomerBody`, so the dialog never sends an address the API rejects.
 */
export const addCustomersFormModel = z.object({
  emails: z.array(personFields.email).check(nonEmpty('validation.emailsRequired')),
})
export type AddCustomersFormModel = z.infer<typeof addCustomersFormModel>

export const isCustomerEmail = (value: string): boolean => personFields.email.safeParse(value).success

/** What `parseEmailList` found in a piece of text, each list in the order it appeared. */
export interface ParsedEmailList {
  /** Valid addresses, normalised and without duplicates. */
  emails: string[]
  /** Words with an `@` that are not valid addresses - a typo worth showing back. */
  rejected: string[]
  /** Words with no `@` at all: a column header ("Email"), a name, a phone number. */
  skipped: string[]
}

/**
 * Reads the addresses out of text a manager typed or pasted into "Pridať klientov". Addresses may be separated by
 * commas, semicolons (Outlook, and Excel's `TEXTJOIN` in a Slovak locale), tabs (a copied spreadsheet row) or line
 * breaks (a copied spreadsheet column); `Jana <jana@example.sk>` and quoted addresses are unwrapped.
 */
export function parseEmailList(text: string): ParsedEmailList {
  const words = text
    .split(/[\s,;]+/)
    .map((word) => word.replace(/^["'<(]+|[>)"']+$/g, ''))
    .filter(Boolean)
  const emails: string[] = []
  const rejected: string[] = []
  const skipped: string[] = []
  for (const word of words) {
    const email = normalizeEmail(word)
    if (!isCustomerEmail(email)) {
      if (word.includes('@')) rejected.push(word)
      else skipped.push(word)
    } else if (!emails.includes(email)) {
      emails.push(email)
    }
  }
  return { emails, rejected, skipped }
}

/**
 * "Nový klient" inside a search (the add-participant dialog): the e-mail the premise pairs clients by is required, the
 * names are optional. `toCreateCustomerBody` drops an empty name, so an existing client is never sent one.
 */
export const newClientFormModel = z.object({
  email: personFields.email,
  firstName: personFields.firstName,
  lastName: personFields.lastName,
})
export type NewClientFormModel = z.infer<typeof newClientFormModel>

/**
 * Starts "Nový klient" from what was typed into the search: an address goes into the e-mail, words into the names
 * (the first is the first name, the rest the surname), and a phone number nowhere - the form has no phone.
 */
export function newClientFromQuery(query: string): NewClientFormModel {
  const text = query.trim()
  if (text.includes('@')) {
    return { email: normalizeEmail(text), firstName: '', lastName: '' }
  }
  if (/^[+\d\s()/-]*$/.test(text)) {
    return { email: '', firstName: '', lastName: '' }
  }
  const [firstName = '', ...rest] = text.split(/\s+/)
  return { email: '', firstName, lastName: rest.join(' ') }
}

export const toCreateCustomerBody = ({ email, firstName, lastName }: NewClientFormModel): CreateCustomerBody => ({
  email: normalizeEmail(email),
  ...(firstName.trim() && { firstName: firstName.trim() }),
  ...(lastName.trim() && { lastName: lastName.trim() }),
})

/**
 * The edit dialog ("Upraviť klienta"). The birth date is the picker's `Date` where the body wants a calendar day, and
 * `gender: null` is "Neuvedené" - hence its own schema and `toUpdateCustomerBody`.
 */
export const customerFormModel = z.object({
  email: personFields.email,
  firstName: personFields.firstName,
  lastName: personFields.lastName,
  phone: personFields.phone,
  birthDate: z.nullable(z.date()),
  gender: z.nullable(personFields.gender),
})
export type CustomerFormModel = z.infer<typeof customerFormModel>

export const toCustomerFormModel = (customer: ManagedCustomer): CustomerFormModel => ({
  email: customer.email,
  firstName: customer.firstName,
  lastName: customer.lastName,
  phone: customer.phone,
  birthDate: customer.birthDate ? fromCalendarDate(customer.birthDate) : null,
  gender: customer.gender ?? null,
})

export const toUpdateCustomerBody = (model: CustomerFormModel): UpdateCustomerBody => ({
  email: model.email,
  firstName: model.firstName,
  lastName: model.lastName,
  phone: model.phone,
  birthDate: model.birthDate ? toCalendarDate(model.birthDate) : null,
  gender: model.gender,
})

/**
 * Only what the dialog changed, so an edit never rewrites a field it did not touch (and an unchanged e-mail never
 * reaches the pairing rule, docs/clients.md).
 */
export const changedCustomerFields = (before: CustomerFormModel, after: CustomerFormModel): UpdateCustomerBody => {
  const was = toUpdateCustomerBody(before)
  const now = toUpdateCustomerBody(after)
  return Object.fromEntries(
    Object.entries(now).filter(([key, value]) => was[key as keyof UpdateCustomerBody] !== value),
  ) as UpdateCustomerBody
}

/** Loyalty points: an empty number input is `null`, hence its own schema. */
export const customerPointsFormModel = z.object({ loyaltyPoints: requiredFormNumber(customerFields.loyaltyPoints) })
export type CustomerPointsFormModel = z.infer<typeof customerPointsFormModel>

export const toLoyaltyPoints = ({ loyaltyPoints }: CustomerPointsFormModel): number =>
  assertFormNumber(loyaltyPoints, 'loyaltyPoints')

/** Assigning a card: the same rule as `setCustomerCardIdBody`, without the `null` that revokes one. */
export const customerCardFormModel = z.object({ cardId: customerFields.cardId })
export type CustomerCardFormModel = z.infer<typeof customerCardFormModel>

/**
 * A visit as the history dialog edits it: the date picker's `Date` and a note, where the body wants
 * an ISO instant and a free-form `data` object - hence the mapper.
 */
export const customerVisitFormModel = z.object({
  date: z.date(),
  note: z.string().check(z.trim(), z.minLength(1)),
})
export type CustomerVisitFormModel = z.infer<typeof customerVisitFormModel>

export const toCustomerVisitBody = ({ date, note }: CustomerVisitFormModel): CreateCustomerVisitBody => ({
  date: date.toISOString(),
  data: { note: note.trim() },
})
